import React, { memo, useContext, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { AppContext } from '@prisjakt/ui-app-context';
import Button from '@prisjakt/ui-button';
import Breadcrumb from '@prisjakt/ui-breadcrumb';
import { PrivacySettingToggles } from '@prisjakt/web-app-config/types/market';
import T from '@prisjakt/ui-translate';

import TrackingSettingCheckbox from './components/tracking-setting';
import PageContent from '../../components/page-content';
import trackingActions from './actions/tracking';
import acceptCookiesAction from '../../components/cookie-banner/actions';
import * as S from './styles';

import { PrivacySettings } from '../../globals/actions/types';
import { AppState } from '../../reducers';

type StateProps = {
  isAuthenticated: boolean;
  settings: PrivacySettings;
};

type DispatchProps = {
  acceptCookies: (version: string) => void;
  trackingToggleChange: (name: keyof PrivacySettings, active: boolean) => void;
  trackingToggleSave: (
    settings: PrivacySettings,
    isAuthenticated: boolean,
    showSuccessPrompt?: boolean
  ) => void;
};

type Props = DispatchProps & StateProps;

const TrackingSettings = (props: Props) => {
  const { t, market, renderMode } = useContext(AppContext);
  const [pulseEnvId, setPulseEnvId] = useState();

  const showSchibstedAdInfo = market.ads?.appnexus?.loadGDPRScript;
  const privacySettings =
    market &&
    market.privacySettings &&
    (market.privacySettings[renderMode] || market.privacySettings.responsive);

  useEffect(() => {
    if (!IS_BROWSER) {
      return;
    }

    // @ts-ignore
    if (typeof window.pulse !== 'function') {
      return;
    }

    // @ts-ignore
    window.pulse(sdk => sdk.getEnvironmentId().then(id => setPulseEnvId(id)));
  }, []);

  return (
    <S.TrackingSettings>
      <PageContent>
        <Breadcrumb
          homeText={t('common', 'breadcrumb.home')}
          path={[
            {
              id: '1',
              name: t('tracking-settings', 'page.title'),
              pathName: '',
            },
          ]}
        />
        {showSchibstedAdInfo && (
          <>
            <S.SectionTitle>
              {t('tracking-settings', 'schibsted-ads.title')}
            </S.SectionTitle>
            <S.ParagraphText>
              <T
                domain="tracking-settings"
                editAdPreferencesLink={
                  <a
                    id="schibsted-ad-settings"
                    href={`https://aboutme.schibsted.com/p/${market.code}/ads/${pulseEnvId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t(
                      'tracking-settings',
                      'schibsted-ads.edit-preferences-link'
                    )}
                  </a>
                }
                adsReadMoreLink={
                  <a
                    href={`https://info.privacy.schibsted.com/${market.code}/s005/`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('tracking-settings', 'schibsted-ads.read-more-link')}
                  </a>
                }
              >
                schibsted-ads.text
              </T>
            </S.ParagraphText>
          </>
        )}
        <S.PageTitle>
          {t('tracking-settings', 'personal-data.title')}
        </S.PageTitle>
        <S.ParagraphText>
          <T
            domain="tracking-settings"
            cookiePolicyLink={
              <Link to={privacySettings?.cookieUrl as string}>
                {t('common', 'cookie-banner.cookie-policy')}
              </Link>
            }
            privacyPolicyLink={
              <Link to={privacySettings?.privacyUrl as string}>
                {t('common', 'cookie-banner.privacy-policy')}
              </Link>
            }
          >
            personal-data.text
          </T>
        </S.ParagraphText>
        <TrackingSettingCheckbox
          title={t('tracking-settings', 'necessary-data.title')}
          name="necessary_data"
          text={t('tracking-settings', 'necessary-data.text')}
          checked={1}
          disabled
          handleChange={() => {}}
        />
        <TrackingSettingCheckbox
          title={t(
            'tracking-settings',
            // @ts-ignore
            `${PrivacySettingToggles.BehaviourTrackingImprovement}.title`
          )}
          name={PrivacySettingToggles.BehaviourTrackingImprovement}
          handleChange={name => {
            props.trackingToggleChange(name, !props.settings[name]);
          }}
          text={t(
            'tracking-settings',
            // @ts-ignore
            `${PrivacySettingToggles.BehaviourTrackingImprovement}.text`
          )}
          checked={
            props.settings[PrivacySettingToggles.BehaviourTrackingImprovement]
          }
        />
        <TrackingSettingCheckbox
          title={t(
            'tracking-settings',
            // @ts-ignore
            `${PrivacySettingToggles.Personalization}.title`
          )}
          name={PrivacySettingToggles.Personalization}
          handleChange={name => {
            props.trackingToggleChange(name, !props.settings[name]);
          }}
          text={t(
            'tracking-settings',
            // @ts-ignore
            `${PrivacySettingToggles.Personalization}.text`
          )}
          checked={props.settings[PrivacySettingToggles.Personalization]}
          disabled={
            !props.settings[PrivacySettingToggles.BehaviourTrackingImprovement]
          }
          indent
        />
        <TrackingSettingCheckbox
          title={t(
            'tracking-settings',
            // @ts-ignore
            `${PrivacySettingToggles.BehaviorTrackingSurveys}.title`
          )}
          name={PrivacySettingToggles.BehaviorTrackingSurveys}
          handleChange={name => {
            props.trackingToggleChange(name, !props.settings[name]);
          }}
          text={t(
            'tracking-settings',
            // @ts-ignore
            `${PrivacySettingToggles.BehaviorTrackingSurveys}.text`
          )}
          checked={
            props.settings[PrivacySettingToggles.BehaviorTrackingSurveys]
          }
        />
        <TrackingSettingCheckbox
          title={t(
            'tracking-settings',
            // @ts-ignore
            `${PrivacySettingToggles.BehaviorTrackingMarketing}.title`
          )}
          name={PrivacySettingToggles.BehaviorTrackingMarketing}
          handleChange={name => {
            props.trackingToggleChange(name, !props.settings[name]);
          }}
          text={t(
            'tracking-settings',
            // @ts-ignore
            `${PrivacySettingToggles.BehaviorTrackingMarketing}.text`
          )}
          checked={
            props.settings[PrivacySettingToggles.BehaviorTrackingMarketing]
          }
        />
        <Button
          color="positive"
          variant="outlined"
          onClick={() => {
            props.acceptCookies(privacySettings.cookieVersion);
            props.trackingToggleSave(
              props.settings,
              props.isAuthenticated,
              true
            );
          }}
        >
          {t('common', 'button.save')}
        </Button>
      </PageContent>
    </S.TrackingSettings>
  );
};

const mapStateToProps = (state: AppState) => ({
  isAuthenticated: state.areas.account.isAuthenticated,
  settings: state.areas.user.tracking.settings,
});

const dispatchActions = {
  acceptCookies: acceptCookiesAction,
  trackingToggleChange: trackingActions.trackingToggleChange,
  trackingToggleSave: trackingActions.trackingToggleSave,
};

export default connect(
  mapStateToProps,
  dispatchActions
)(memo(TrackingSettings));
